import React from 'react';
import {useTranslation} from 'react-i18next';
import {IElementQuestionAnswer, IQuestionAnswerType} from '../../../../../incase-domain/elements/base/questionElementBase';
import {IElementQuestionYesNo} from '../../../../../incase-domain/elements/question/questionYesNo';
import ChangeAnswer from '../answers/actions/ChangeAnswer';
import AnswerActions, {AnswerActionType} from '../answers/AnswerActions';
import {IValidationErrorObject} from '../../../../../incase-domain/common/validationErrorResponse';

type YesNoAnswerGroupProps = {
    index: 0 | 1;
    errors: IValidationErrorObject | null;
    highlightedElement: IElementQuestionYesNo;
    highlightedElementChangeHandler: {
        single: (field: keyof IElementQuestionYesNo, value: unknown) => void;
        multiple: (fields: (keyof IElementQuestionYesNo)[], values: unknown[]) => void;
    };
};

function YesNoAnswerGroup({index, errors, highlightedElement, highlightedElementChangeHandler}: YesNoAnswerGroupProps): JSX.Element {
    const {t} = useTranslation();

    const determineAnswerType = (correctValue: number, actualValue: number, isDebatable?: boolean): IQuestionAnswerType => {
        if (isDebatable) {
            return 'debatable';
        }
        if (correctValue === actualValue) {
            return 'correct';
        }
        return 'incorrect';
    };

    const yesAnswer = {
        text: t('designSidepanel.yes'),
        type: determineAnswerType(highlightedElement.correct, 1, highlightedElement.isDebatable),
        comment: highlightedElement.comment.yes,
    };

    const noAnswer = {
        text: t('designSidepanel.no'),
        type: determineAnswerType(highlightedElement.correct, 0, highlightedElement.isDebatable),
        comment: highlightedElement.comment.no,
    };

    const mockQuestionnaire = {
        ...highlightedElement,
        answers: [noAnswer, yesAnswer],
        singleCorrect: !highlightedElement.isDebatable,
        correctAnswers: [],
        haveTryButton: false,
        tryLabel: '',
        writeAnswerIndices: false,
    };

    const handleCommentChange = (index: number, comment: string): void => {
        if (index === 0) {
            highlightedElementChangeHandler.single('comment', {
                ...highlightedElement.comment,
                no: comment,
            });
        } else {
            highlightedElementChangeHandler.single('comment', {
                ...highlightedElement.comment,
                yes: comment,
            });
        }
    };

    const handleSingleChange = (field: string, value: unknown): void => {
        if (field === 'answers') {
            const newAnswers = value as IElementQuestionAnswer[];
            const {type} = newAnswers[index];

            if (type === 'debatable') {
                highlightedElementChangeHandler.single('isDebatable', true);
            } else if (type === 'correct') {
                highlightedElementChangeHandler.multiple(['isDebatable', 'correct'], [false, index === 1 ? 1 : 0]);
            } else {
                highlightedElementChangeHandler.multiple(['isDebatable', 'correct'], [false, index === 1 ? 0 : 1]);
            }
        } else {
            highlightedElementChangeHandler.single(field as keyof IElementQuestionYesNo, value);
        }
    };

    const handleCommentOnlyChange = (field: string, value: unknown): void => {
        if (field === 'answers') {
            const newAnswers = value as IElementQuestionAnswer[];
            if (newAnswers[index].comment !== (index === 0 ? highlightedElement.comment.no : highlightedElement.comment.yes)) {
                handleCommentChange(index, newAnswers[index].comment);
            }
        } else {
            highlightedElementChangeHandler.single(field as keyof IElementQuestionYesNo, value);
        }
    };

    return (
        <>
            <ChangeAnswer
                answer={mockQuestionnaire.answers[index].text}
                validationErrors={errors}
                inactive
                index={index}
                highlightedElement={mockQuestionnaire}
                highlightedElementChangeHandler={{single: handleSingleChange}}
            />
            <AnswerActions
                index={index}
                validationErrors={errors}
                highlightedElement={{
                    ...mockQuestionnaire,
                    answers: [
                        {...mockQuestionnaire.answers[0], comment: highlightedElement.comment.no},
                        {...mockQuestionnaire.answers[1], comment: highlightedElement.comment.yes},
                    ],
                }}
                highlightedElementChangeHandler={{single: handleCommentOnlyChange}}
                actions={[AnswerActionType.CHANGE_COMMENT, AnswerActionType.ANSWER_RULES]}
            />
        </>
    );
}

export default YesNoAnswerGroup;
